/**
 * Client-side Place model with computed properties for UI.
 */

import type { MapPoint } from '@elara/client-core/ui-contracts/map-point';
import type { WidgetImage } from '@elara/domain/models/schemas/image-schemas';

import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type { ClientInstanceOf } from '#firebase/types.js';

import { PLACE_STALE_MS } from '@elara/domain/constants';
import { PlaceModelSpec } from '@elara/domain/models/PlaceModelSpec';
import { placeMapsUrl } from '@elara/domain/models/place-maps-url';
import { describeOpeningStatus, isOpenAt, type OpeningStatus } from '@elara/domain/opening-hours';

import { createClientModel } from '#firebase/createClientModel.js';
import { toTimestampMs } from '#lib/date-utils.js';
import { resolveMediaUrl } from '#lib/media-url.js';
import { getNowInTimeZone } from '#lib/timezone-utils.js';

export function createClientPlaceModel(firestore: FirestoreContainer) {
  return createClientModel(firestore, PlaceModelSpec, () => ({
    get displayName(): string {
      return this.name ?? 'Unknown Place';
    },

    get shortAddress(): string {
      const parts = [this.address?.line1, this.address?.city, this.address?.country].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : '';
    },

    get mapPoint(): MapPoint {
      return {
        id: this.id,
        label: this.displayName,
        latitude: this.coordinates?.lat,
        longitude: this.coordinates?.lng,
        address: this.formattedAddress ?? this.shortAddress,
      };
    },

    get primaryImage(): string | undefined {
      return this.allImages?.[0]?.url;
    },

    get allImages(): WidgetImage[] {
      // New-format photos store media-proxy paths; resolve them for the current platform.
      // Legacy docs carry absolute Google URLs which pass through untouched (they heal to the
      // proxy format on the next staleness refresh).
      return (this.photos ?? []).map((image) => ({ ...image, url: resolveMediaUrl(image.url) }));
    },

    get contactLinks(): { phone?: string; website?: string; } {
      return {
        phone: this.internationalPhoneNumber ?? this.phoneNumber ?? undefined,
        website: this.website ?? undefined,
      };
    },

    /**
     * Canonical Google Maps page for this place. For venues with a Google booking partner
     * (OpenTable, Resy, SevenRooms, Tock) that page carries Google's own Reserve button, which
     * is the only reservation route we can offer without inventing a URL. See
     * `placeMapsUrl` for why no direct booking link exists and why the label must stay neutral.
     */
    get mapsUrl(): string | undefined {
      return placeMapsUrl({ ids: this.ids, googleMapsLinks: this.googleMapsLinks });
    },

    /**
     * Google's per-person spend band as display text ("$100+", "$50-100"). Distinct from the
     * coarse `priceLevel` enum, and the number a client actually reasons about for a restaurant.
     */
    get priceRangeLabel(): string | undefined {
      const range = this.priceRange;
      if (!range) return undefined;
      const { startPrice, endPrice, currencyCode } = range;
      if (startPrice == null && endPrice == null) return undefined;

      const format = (amount: number) => {
        if (!currencyCode) return String(amount);
        try {
          // 'en-US' matches every other Intl call in client-core (ShortlistItemModel.formattedCost,
          // lib/utils, date-utils). Device locale here would format the price band differently
          // from the shortlist cost on the same screen.
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode,
            maximumFractionDigits: 0,
          }).format(amount);
        } catch {
          // Intl throws on a malformed currency code; the bare number still reads fine.
          return String(amount);
        }
      };

      if (startPrice != null && endPrice != null) return `${format(startPrice)}-${format(endPrice)}`;
      if (startPrice != null) return `${format(startPrice)}+`;
      return `Up to ${format(endPrice as number)}`;
    },

    get type(): string {
      return this.primaryType ?? this.types?.[0] ?? 'place';
    },

    get primaryCategory(): string | undefined {
      return this.type?.replaceAll('-', ' ').replaceAll('_', ' ');
    },

    get isOpenNow(): boolean {
      // `getNowInTimeZone` shifts "now" into the place's local wall-clock so the shared
      // `isOpenAt` algorithm reads the right weekday/hour against Google's weekly periods.
      return isOpenAt(this.openingHours?.periods, getNowInTimeZone(this.timeZone));
    },

    /**
     * Open/closed plus the next transition ("Closes 5 PM" / "Opens 8:30 AM Thu"), or undefined when
     * the place has no usable schedule. Drives the Google-Maps-style hours row in the itinerary card.
     */
    get openingStatus(): OpeningStatus | undefined {
      const periods = this.openingHours?.periods;
      if (!periods?.length) return undefined;
      return describeOpeningStatus(periods, getNowInTimeZone(this.timeZone));
    },

    /** Whether this place needs AI-generated description/advisory. */
    get needsEnrichment(): boolean {
      const generated = this.generated as { generatedAt?: unknown } | null | undefined;
      const generatedAt = generated?.generatedAt;
      if (!generatedAt) return true;
      const fetchedAt = this.fetchedAt;
      if (!fetchedAt) return false;
      return toTimestampMs(fetchedAt) > toTimestampMs(generatedAt);
    },

    /**
     * Whether the cached provider data is older than the server's refresh TTL.
     * Mirrors STALE_MS in server/src/places/get-or-refresh-place.ts so callers
     * can avoid issuing a refresh flow when the server would no-op anyway.
     */
    get isStale(): boolean {
      const fetchedAtMs = toTimestampMs(this.fetchedAt);
      if (!fetchedAtMs) return true;
      return Date.now() - fetchedAtMs > PLACE_STALE_MS;
    },

    get relatedPlaceIds(): string[] {
      const relatedIds = new Set<string>();

      // Add sub-destinations
      if (this.subDestinationIds) {
        for (const id of this.subDestinationIds) relatedIds.add(id);
      }

      // Add parent destination
      if (this.containingPlaceIds) {
        for (const id of this.containingPlaceIds) relatedIds.add(id);
      }

      return [...relatedIds];
    },
  }));
}

export type ClientPlaceModel = ReturnType<typeof createClientPlaceModel>;
export type Place = ClientInstanceOf<ClientPlaceModel>;
